/**
 * Glyph maps: stable backend string keys → display emoji. Emoji are NOT user-facing translatable
 * copy (they are locale-independent pictographs), so they live here, not in the i18n catalogs. A
 * missing/unknown key falls back to a neutral glyph so the UI never renders a raw code.
 */

const AVATAR_GLYPHS: Record<string, string> = {
  fox: '🦊',
  cat: '🐱',
  bear: '🐻',
  bunny: '🐰',
  fox2: '🦝',
  owl: '🦉',
  panda: '🐼',
  penguin: '🐧',
  dog: '🐕',
  tiger: '🐯',
  frog: '🐸',
  koala: '🐨',
  monkey: '🐵',
  unicorn: '🦄',
  dragon: '🐉',
  hedgehog: '🦔',
};

/** Emoji for an avatar key; falls back to a smiley for unknown keys. */
export function avatarGlyph(key: string | null | undefined): string {
  return (key && AVATAR_GLYPHS[key]) || '🙂';
}

const CHORE_GLYPHS: Record<string, string> = {
  dog: '🐕',
  trash: '🗑️',
  bed: '🛏️',
  teeth: '🪥',
  homework: '📚',
  room: '🧹',
  table: '🍽️',
  dishes: '🍴',
  plants: '🪴',
  laundry: '🧺',
  read: '📖',
  water: '💧',
  pet: '🐾',
  clothes: '👕',
  backpack: '🎒',
  spray: '🧴',
  groceries: '🛒',
  cooking: '🍳',
  leaves: '🍂',
  mail: '📬',
  // Expansion set (2026-06-24) — bespoke art in assets/icons/chore/; emoji is the fallback only.
  vacuum: '🧹',
  mop: '🧽',
  dust: '🪶',
  windows: '🪟',
  bathroom: '🛁',
  sink: '🚰',
  mirror: '🪞',
  dishwasher: '🫧',
  fridge: '🧊',
  lunchbox: '🍱',
  recycle: '♻️',
  fold: '👚',
  iron: '👔',
  hangers: '🧥',
  shoes: '👟',
  lawn: '🌿',
  snow: '❄️',
  weeds: '🌾',
  car: '🚗',
  'watering-can': '💦',
  'feed-pet': '🦴',
  'walk-dog': '🦮',
  litter: '🐈',
  fish: '🐟',
  shower: '🚿',
  hair: '🪮',
  soap: '🧼',
  medicine: '💊',
  instrument: '🎸',
  help: '🤝',
  baby: '🍼',
};

/** Emoji for a chore/template icon key; falls back to a sparkle for unknown keys. */
export function choreGlyph(key: string | null | undefined): string {
  return (key && CHORE_GLYPHS[key]) || '✨';
}

const HABIT_GLYPHS: Record<string, string> = {
  teeth: '🪥',
  read: '📖',
  water: '💧',
  exercise: '🤸',
  sleep: '😴',
  tidy: '🧹',
  veggies: '🥦',
  stretch: '🧘',
  music: '🎵',
  outside: '🌳',
  gratitude: '💛',
  star: '⭐',
  soap: '🧼',
  apple: '🍎',
  shower: '🚿',
  pencil: '✏️',
  heart: '❤️',
  hands: '🤝',
  screen: '📱',
  screen_off: '📵',
  // Expansion (2026-06-24) — art via habitArt (16 reused pack icons + 4 new in habit/); emoji is the fallback.
  bed: '🛏️',
  dishes: '🍴',
  plants: '🪴',
  'walk-dog': '🦮',
  recycle: '♻️',
  clothes: '👕',
  backpack: '🎒',
  bike: '🚲',
  sport: '⚽',
  breakfast: '🥞',
  vitamins: '💊',
  wake: '🌅',
  hair: '🪮',
  homework: '📚',
  instrument: '🎸',
  friends: '👫',
  smile: '😊',
  journal: '📔',
  dance: '💃',
  floss: '🦷',
};

/** Emoji for a habit icon key; falls back to a sprouting plant (kind growth) for unknown keys. */
export function habitGlyph(key: string | null | undefined): string {
  return (key && HABIT_GLYPHS[key]) || '🌱';
}

const REWARD_GLYPHS: Record<string, string> = {
  icecream: '🍦',
  movie: '🎬',
  game: '🎮',
  toy: '🧸',
  book: '📕',
  screen: '📱',
  outing: '🎡',
  treat: '🍪',
  money: '💵',
  sticker: '⭐',
};

/** Emoji for a reward icon key; falls back to a gift for unknown keys. */
export function rewardGlyph(key: string | null | undefined): string {
  return (key && REWARD_GLYPHS[key]) || '🎁';
}

/**
 * Per-TOPIC emoji for the kid learning map (ADR-0034) — gives each topic its own identity on the path
 * instead of a generic lock/arrow. Keyed on the catalog topic id (the curriculum's `prereqTopics`/`order`
 * already drive the path; this is just the face). A custom PACK topic (`pack:…`) or any unknown id falls
 * back to a friendly book.
 */
const TOPIC_GLYPHS: Record<string, string> = {
  'math.spatial': '🧭',
  'math.shapes': '🔷',
  'math.counting': '🔢',
  'math.compare-10': '⚖️',
  'math.number-bonds': '🔗',
  'math.add-within-10': '➕',
  'math.subtraction': '➖',
  'math.skip-count': '🖐️',
  'math.numbers-to-20': '🔟',
  'math.numbers-to-100': '💯',
  'math.measure': '📏',
  'math.money': '💰',
  'math.time-units': '🗓️',
  'math.add-within-20': '➕',
  'math.add-sub-100': '🧮',
  'math.multiplication': '✖️',
  'math.times-tables-easy': '✖️',
  'math.times-tables-hard': '✖️',
  'math.mul-compare': '📈',
  'math.expressions': '🟰',
  'math.word-problems': '📝',
  'math.word-problems-multi': '🧩',
  'math.geometry': '📐',
  'math.perimeter': '🟦',
  'math.data': '📊',
};

/** Emoji for a learning topic; falls back to a book for pack/unknown topics. */
export function topicGlyph(topicId: string | null | undefined): string {
  return (topicId && TOPIC_GLYPHS[topicId]) || '📘';
}

const SUBJECT_GLYPHS: Record<string, string> = {
  math: '🔢',
  reading: '📖',
  time: '🕐', // no built-in `time` subject (ADR-0074); kept for a guardian-authored `time` pack lane
  ukr: '📝',
  eng: '💬',
  svit: '🌱',
};

/** Emoji for a learning subject (the root-category card); falls back to a puzzle piece for packs. */
export function subjectGlyph(subject: string | null | undefined): string {
  return (subject && SUBJECT_GLYPHS[subject]) || '🧩';
}
